/**
 * Service for managing mobile menu state and navigation.
 * Provides centralized menu visibility control and event notifications.
 */

import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Service for managing mobile menu state and visibility.
 *
 * @remarks Provides centralized control over the mobile navigation menu state.
 * Manages menu visibility, toggle functionality, and emits events for menu
 * state changes that components can subscribe to for coordinated behavior.
 */
@Injectable({
  providedIn: 'root',
})
export class MenuService {
  /** @remarks Current visibility state of the menu (true = hidden, false = visible) */
  menuHidden = true;

  /** @remarks Subject that emits when the menu is opened */
  menuOpenEvent = new Subject<void>();

  /** @remarks Subject that emits when the menu is closed */
  menuCloseEvent = new Subject<void>();

  /**
   * Toggles the menu visibility state.
   *
   * @remarks Switches between open and closed menu states and emits
   * the appropriate event (menuOpenEvent or menuCloseEvent) to notify
   * subscribers of the state change.
   */
  toggleMenu(): void {
    this.menuHidden = !this.menuHidden;
    if (!this.menuHidden) {
      this.menuOpenEvent.next();
    } else {
      this.menuCloseEvent.next();
    }
  }

  /**
   * Shows the menu.
   *
   * @remarks Sets the menu to visible state and emits the menuOpenEvent
   * to notify subscribers that the menu has been opened.
   */
  showMenu(): void {
    this.menuHidden = false;
    this.menuOpenEvent.next();
  }

  /**
   * Closes the menu.
   *
   * @remarks Sets the menu to hidden state and emits the menuCloseEvent
   * to notify subscribers that the menu has been closed.
   */
  closeMenu(): void {
    this.menuHidden = true;
    this.menuCloseEvent.next();
  }
}
